import { useState } from "react";
import { PauseCircle } from "lucide-react";

import {
  BatchOnHoldReasonCallout,
  buildBatchOnHoldAnchorSelector,
} from "@/components/manufacturing/BatchOnHoldReasonCallout";
import { Button } from "@/components/ui/Button";
import { HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { MANUFACTURING_HUB_READ_ONLY } from "@/pages/manufacturing/workOrdersPageModel";

export interface BatchOnHoldControlsProps {
  batchId: number;
  shipmentOnHold: boolean;
  hubWritesAllowed: boolean;
  /** When false, hold applies only at SGS — controls are hidden. */
  holdEligible?: boolean;
  /** Layout: inline actions in a header row, or compact block above content. */
  variant?: "header" | "inline";
}

export function BatchOnHoldControls({
  batchId,
  shipmentOnHold,
  hubWritesAllowed,
  holdEligible = true,
  variant = "header",
}: BatchOnHoldControlsProps): JSX.Element | null {
  const [holdCallout, setHoldCallout] = useState<{ shipmentOnHold: boolean } | null>(null);

  if (!holdEligible) {
    return null;
  }

  const toggleLabel = shipmentOnHold ? "Release shipment hold" : "Place shipment hold";
  const anchorKey = buildBatchOnHoldAnchorSelector(batchId);

  return (
    <>
      <div
        className={cn(
          "flex flex-wrap items-center gap-2",
          variant === "inline" && "rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2",
        )}
        data-batch-on-hold-controls={String(batchId)}
      >
        {shipmentOnHold ? (
          <HoverHintTrigger
            align="start"
            trigger={
              <span
                className={cn(
                  "inline-flex shrink-0 items-center justify-center rounded border border-amber-300 bg-amber-50 text-amber-900 outline-none",
                  "cursor-help hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-brand-500",
                  variant === "header" ? "h-8 w-8" : "h-6 w-6",
                )}
                tabIndex={0}
                aria-label="Shipment hold. Manufacturing continues; pre-pack and outbound hide this batch until released."
              >
                <PauseCircle className={variant === "header" ? "h-4 w-4" : "h-3.5 w-3.5"} aria-hidden />
              </span>
            }
          >
            <div className="max-w-[18rem] whitespace-normal text-left text-sm leading-snug">
              <div className="font-semibold text-amber-900">Shipment hold</div>
              <p className="mt-1 text-slate-700">
                Manufacturing continues. Pre-pack and outbound hide this batch until shipment hold is released.
              </p>
            </div>
          </HoverHintTrigger>
        ) : null}
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY}>
          <Button
            type="button"
            size="sm"
            variant={shipmentOnHold ? "secondary" : "primary"}
            disabled={!hubWritesAllowed}
            onClick={() => setHoldCallout({ shipmentOnHold: !shipmentOnHold })}
          >
            {toggleLabel}
          </Button>
        </ReadOnlyHoverLayer>
      </div>
      {holdCallout != null ? (
        <BatchOnHoldReasonCallout
          batchId={batchId}
          shipmentOnHold={holdCallout.shipmentOnHold}
          hubWritesAllowed={hubWritesAllowed}
          anchorSelector={anchorKey}
          onClose={() => setHoldCallout(null)}
        />
      ) : null}
    </>
  );
}
